import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import SocialShare from "./SocialShare";
import BrandLogo from "./BrandLogo";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="bg-white border-t border-zinc-200 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-2 font-extrabold text-zinc-950 mb-3">
              <BrandLogo variant="mark" size={28} />
              <span className="text-base">
                fastprototype<span className="bg-gradient-to-r from-emerald-400 to-sky-500 bg-clip-text text-transparent">.ai</span>
              </span>
            </div>
            <p className="text-zinc-500 text-sm leading-relaxed max-w-xs">{t("footer.tagline")}</p>
            <div className="flex items-center gap-2 mt-4 text-xs text-zinc-500 flex-wrap">
              <Shield size={12} className="text-green-600" />
              <span className="font-semibold text-green-700">GDPR Compliant</span>
              <span className="text-zinc-300">|</span>
              <span>NDA Included</span>
              <span className="text-zinc-300">|</span>
              <span>Client Owns IP</span>
            </div>
            <div className="flex items-center gap-1.5 mt-2 text-xs text-zinc-500" data-testid="footer-eu-badge">
              <span aria-hidden="true">🇪🇺</span> {t("footer.based")}
            </div>
            <div className="mt-5" data-testid="footer-social-share">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">{t("footer.share")}</div>
              <SocialShare compact />
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">Services</div>
            <ul className="space-y-2">
              <li><Link to="/rapid-mvp-development" data-testid="footer-rapid-mvp" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Rapid MVP Development</Link></li>
              <li><Link to="/mvp-development-for-startups" data-testid="footer-mvp-startups" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">MVP for Startups</Link></li>
              <li><Link to="/fixed-price-mvp-development" data-testid="footer-fixed-price" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Fixed-Price MVP</Link></li>
              <li><Link to="/pricing" data-testid="footer-pricing" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Pricing</Link></li>
              <li><Link to="/build" data-testid="footer-build" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Build My Prototype</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">Guides</div>
            <ul className="space-y-2">
              <li><Link to="/guides/mvp-development-cost" data-testid="footer-guide-cost" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">MVP Development Cost</Link></li>
              <li><Link to="/guides/how-long-does-an-mvp-take" data-testid="footer-guide-timeline" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">How Long Does an MVP Take</Link></li>
              <li><Link to="/guides/prototype-vs-poc-vs-mvp" data-testid="footer-guide-terminology" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Prototype vs POC vs MVP</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">{t("footer.company")}</div>
            <ul className="space-y-2">
              <li><Link to="/about" data-testid="footer-about-link" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">{t("nav.about")}</Link></li>
              <li><a href="/#how-it-works" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">{t("nav.how_it_works")}</a></li>
              <li><Link to="/customer/login" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">{t("nav.my_projects")}</Link></li>
              <li><Link to="/promoters" data-testid="footer-promoter-apply" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Promoters</Link></li>
              <li><a href="/legal#nda" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">NDA</a></li>
              <li><a href="/legal#ip" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">IP Ownership</a></li>
              <li><a href="/legal#terms" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Terms of Service</a></li>
              <li><a href="/legal#gdpr" target="_blank" rel="noopener noreferrer" data-testid="footer-gdpr-link" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">GDPR / Privacy</a></li>
              <li>
                <Link to="/admin/login" data-testid="footer-team-login" className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
                  Team Login
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-400">© 2026 FastPrototype.ai. {t("footer.rights")}</p>
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            <button data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new CustomEvent("lf:open-cookie-settings"))} className="hover:text-zinc-700 transition-colors">{t("footer.cookie_settings")}</button>
            <span className="text-zinc-200">·</span>
            <span>{t("footer.contact")}: <a href="mailto:hello@fastprototype.ai" className="hover:text-zinc-700 transition-colors">hello@fastprototype.ai</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
}
